import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getAllSettingsService, updateAdminSetting } from '../services/Settings.service';

// These setting keys are read by the server at dispatch time (PrintBatch.service.dispatchBatch /
// getDispatchMode). The webhook URL + secret are the outbound half of the N8N contract; the
// callback token and SERVER_API_URL are env vars, not settings — see the n8n README.
const KEYS = {
  mode: { key: 'print-dispatch-mode', title: 'Print dispatch mode' },
  url: { key: 'print-dispatch-webhook-url', title: 'Print dispatch webhook URL' },
  secret: { key: 'print-dispatch-webhook-secret', title: 'Print dispatch webhook secret' },
  // Diagnostics only — the server never dispatches through this. N8N holds its own copy of the
  // Lambda URL; storing it here lets the connectivity panel probe the zipper.
  zipper: { key: 'print-zipper-url', title: 'PDF zipper Lambda URL' },
};

const MODE_OPTIONS = [
  { value: 'manual_batch', label: 'Manual', hint: 'Admins create & dispatch print jobs by hand (from All Orders or an order card). The default.' },
  { value: 'auto_batch', label: 'Automatic', hint: 'A cron auto-creates and dispatches print jobs from ready-to-dispatch orders every 30 min; manual dispatch still available.' },
];

type Field = 'mode' | 'url' | 'secret' | 'zipper';

const PrintDispatchSettings: React.FC = () => {
  const [mode, setMode] = useState('manual_batch');
  const [url, setUrl] = useState('');
  const [secretInput, setSecretInput] = useState('');
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [zipperUrl, setZipperUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Field | null>(null);

  const fetchCurrent = async () => {
    setLoading(true);
    try {
      const settings = await getAllSettingsService();
      const find = (id: string) => settings.find((s: any) => s.id === id);
      // Coerce unknown/retired stored values (e.g. a pre-cutover 'legacy_auto') to the default so
      // the controlled select never renders blank and Save never re-submits a rejected value.
      const storedMode = String(find(KEYS.mode.key)?.settingValue || '');
      setMode(MODE_OPTIONS.some((o) => o.value === storedMode) ? storedMode : 'manual_batch');
      setUrl(String(find(KEYS.url.key)?.settingValue || ''));
      // The secret value is never round-tripped into the input — we only show whether it's set.
      setSecretConfigured(!!find(KEYS.secret.key)?.settingValue);
      setZipperUrl(String(find(KEYS.zipper.key)?.settingValue || ''));
    } catch {
      // leave defaults; the fields are still usable to set values
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrent();
  }, []);

  const save = async (field: Field) => {
    const cfg = KEYS[field];
    const value =
      field === 'mode' ? mode : field === 'url' ? url.trim() : field === 'zipper' ? zipperUrl.trim() : secretInput;
    if (field === 'secret' && !value) {
      toast.error('Enter a secret value to save');
      return;
    }
    if ((field === 'url' || field === 'zipper') && value && !/^https?:\/\//i.test(value)) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    setSaving(field);
    try {
      await updateAdminSetting(cfg.key, cfg.title, value);
      toast.success(`${cfg.title} saved`);
      if (field === 'secret') {
        setSecretInput('');
        setSecretConfigured(true);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const btn = 'bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50';

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-1">Print Dispatch</h2>
      <p className="text-sm text-gray-500 mb-4">
        How print jobs are sent to the printer via the N8N workflow.
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="flex flex-col gap-5 max-w-2xl">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Dispatch mode</label>
            <div className="flex items-center gap-2">
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                {MODE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => save('mode')} disabled={saving === 'mode'} className={btn}>
                Save
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">{MODE_OPTIONS.find((o) => o.value === mode)?.hint}</p>
            <p className="text-xs text-amber-600 mt-1">
              Heads-up: when the N8N workflow runs with email_mode=draft, orders show “In production” as soon as
              the Gmail draft is created — before anyone sends it to the printer. A forgotten draft overstates
              every status surface. Use email_mode=send for truthful statuses, or make sending the draft part of
              the daily routine.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Webhook URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://n8n.example.com/webhook/print-batch-dispatch"
                className="border border-gray-300 rounded px-2 py-1.5 text-sm w-96 max-w-full"
              />
              <button type="button" onClick={() => save('url')} disabled={saving === 'url'} className={btn}>
                Save
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">The N8N production webhook the server POSTs each batch to.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Webhook secret{' '}
              {secretConfigured ? (
                <span className="text-storygreen">• configured</span>
              ) : (
                <span className="text-gray-400">• not set</span>
              )}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder={secretConfigured ? 'Enter a new value to replace' : 'Enter secret'}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm w-96 max-w-full"
              />
              <button type="button" onClick={() => save('secret')} disabled={saving === 'secret'} className={btn}>
                Save
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Sent to N8N as the <code>X-BMS-Token</code> header — must match the Header Auth credential in the workflow.
              Not shown again once saved.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">PDF zipper Lambda URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={zipperUrl}
                onChange={(e) => setZipperUrl(e.target.value)}
                placeholder="https://xxxxx.lambda-url.us-east-1.on.aws/"
                className="border border-gray-300 rounded px-2 py-1.5 text-sm w-96 max-w-full"
              />
              <button type="button" onClick={() => save('zipper')} disabled={saving === 'zipper'} className={btn}>
                Save
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Optional, for diagnostics only: N8N calls the zipper, not the server. Setting it here lets the
              connectivity panel (Settings → Testing) confirm the Lambda is deployed and answering.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintDispatchSettings;
